import { shouldNeverHappen, switchOn } from '@moojo/misc-constructs'
import { isValid, parseISO } from 'date-fns'
import { z } from 'zod'

import type { DurationUnit } from './duration'
import { Duration, durationOf } from './duration'

/**
 * An immutable point in time, in milliseconds granularity.
 */
export class Instant {
  /**
   * @returns the instant representing 1970-01-01T00:00:00Z
   */
  static get epoch() {
    return epoch
  }

  /**
   * Number of milliseconds since 1-1-1970 (UTC).
   */
  readonly millis: number

  constructor(date: Date)
  constructor(millisSince1970: number)
  constructor(input: Date | number) {
    if (!isValid(input)) {
      throw new Error(`Invalid Instant input: ${input}`)
    }
    this.millis = new Date(input).getTime()
  }

  public static fromMillis(millisSince1970: number) {
    return new Instant(millisSince1970)
  }

  // #TD(imaman): unite all the from() methods into a single one.
  public static fromIsoString(iso8601Timestamp: string) {
    const date = parseISO(iso8601Timestamp)
    if (!isValid(date)) {
      throw new Error(`Invalid ISO-8601 timestamp: ${iso8601Timestamp}`)
    }
    return new Instant(date)
  }

  public static fromDate(date: Date) {
    return new Instant(date)
  }

  /**
   * @returns a Date object matching the instant.
   */
  get date() {
    return new Date(this.millis)
  }

  /**
   * @returns a duration from epoch to this instant
   */
  get duration() {
    return this.minus(epoch)
  }

  /**
   * @returns Number of seconds since 1-1-1970 (UTC).
   */
  get seconds() {
    return Math.round(this.millis / 1000)
  }

  /**
   * @returns the ISO-8601 representation of the instant.
   */
  format() {
    return this.date.toISOString()
  }

  toJSON() {
    return this.millis
  }

  toString() {
    return this.format()
  }

  static zod = (units: 'millis' | 'seconds' = 'millis') => {
    let toMillis: number
    if (units === 'millis') {
      toMillis = 1
    } else if (units === 'seconds') {
      toMillis = 1000
    } else {
      shouldNeverHappen(units)
    }

    return z.union([
      z.instanceof(Instant),
      z.number().transform(n => {
        return Instant.fromMillis(n * toMillis)
      }),
      z.string().transform(s => Instant.fromIsoString(s)),
      z.object({ millis: z.number() }).transform(o => Instant.fromMillis(o.millis)),
    ])
  }

  public is(operator: '<' | '<=' | '==' | '>=' | '>', other: Instant) {
    const c = this.compare(other)
    return switchOn(operator, {
      '<': () => c < 0,
      '<=': () => c <= 0,
      '==': () => c == 0,
      '>=': () => c >= 0,
      '>': () => c > 0,
    })
  }

  public compare(other: Instant): -1 | 0 | 1 {
    if (this.millis < other.millis) {
      return -1
    }
    if (this.millis > other.millis) {
      return 1
    }
    return 0
  }

  /**
   * Adds a duration to this instant
   * @param the duration to add
   * @returns A new Instant object
   */
  public shiftBy(duration: Duration): Instant
  public shiftBy(amount: number, unit: DurationUnit): Instant
  public shiftBy(...a: [Duration] | [number, DurationUnit]): Instant {
    const d = a.length === 1 ? a[0] : durationOf(a[0], a[1])
    const combined = this.duration.plus(d)
    return instantOf(combined)
  }

  /**
   * Computes the difference between `this` and `rhs`
   * @param rhs the Insant to subtract
   * @returns The duration between `rhs` and `this`
   */
  public minus(rhs: Instant): Duration {
    const diff = this.millis - rhs.millis
    return durationOf(diff, 'millis')
  }

  public static max(first: Instant, ...others: Instant[]): Instant {
    let maxInstant = first
    for (const other of others) {
      maxInstant = maxInstant.millis >= other.millis ? maxInstant : other
    }
    return maxInstant
  }

  public static min(first: Instant, ...others: Instant[]): Instant {
    let minInstant = first
    for (const other of others) {
      minInstant = minInstant.millis <= other.millis ? minInstant : other
    }
    return minInstant
  }
}

export function instantOf(iso8601Timestamp: string): Instant
export function instantOf(millisSince1970: number): Instant
export function instantOf(date: Date): Instant
export function instantOf(durationSince1970: Duration): Instant
export function instantOf(instant: Instant): Instant
export function instantOf(input: string | number | Date | Duration | Instant): Instant
export function instantOf(input: string | number | Date | Duration | Instant) {
  if (input instanceof Instant) {
    return input
  }
  input = input instanceof Duration ? input.whole('millis') : input
  return Instant.fromDate(new Date(input))
}

const epoch = instantOf(0)
